import type { IModuleConfig } from 'types/pipeline';

// 目标语料集模式枚举
export const TARGET_CORPUS_SET_MODE = {
  NEW: 1,
  EXISTING: 2,
} as const;

export const CORPUS_SET_TYPE_MAP = {
  INITIAL: {
    value: 1,
    text: '原始语料集',
    color: 'volcano',
  },
  MACHINE_SCREENED: {
    value: 2,
    text: '机筛语料集',
    color: 'purple',
  },
  FINE_SCREENED: {
    value: 3,
    text: '精筛语料集',
    color: 'lime',
  },
  FINE_ANNOTATED: {
    value: 4,
    text: '精标语料集',
    color: 'blue',
  },
  MACHINE_MODIFIED: {
    value: 5,
    text: '机修语料集',
    color: 'cyan',
  },
};

export const TASK_TYPE_MAP = {
  CRAWL: {
    value: 1,
    text: '抓取任务',
    pathKey: 'crawl',
  },
  CLEAN: {
    value: 2,
    text: '清洗任务',
    pathKey: 'corpus-cleaning',
  },
  UPLOAD: {
    value: 3,
    text: '上传任务',
    pathKey: 'local-upload',
  },
  MACHINE_MODIFY: {
    value: 4,
    text: '修改任务',
    pathKey: 'machine-modification',
  },
  HUMAN_FILTER: {
    value: 5,
    text: '人筛任务',
    pathKey: 'human-filter',
  },
  MACHINE_LABEL: {
    value: 6,
    text: '机标任务',
    pathKey: 'machine-label',
  },
  MACHINE_COMMON: {
    value: 7,
    text: '语料处理链路',
    pathKey: 'machine-common',
  },
};

// 语料处理链路规则类型
export const RULE_TYPE_MAP = {
  CLEAN: {
    value: 'clean',
    text: '数据筛选规则',
    taskType: 2, // TASK_TYPE_MAP.CLEAN.value
  },
  MODIFY: {
    value: 'modify',
    text: '数据清洗规则',
    taskType: 4, // TASK_TYPE_MAP.MACHINE_MODIFY.value
  },
  ANNOTATION: {
    value: 'annotation',
    text: '数据标注规则',
    taskType: 6, // TASK_TYPE_MAP.MACHINE_LABEL.value
  },
} as const;

// 默认规则顺序
export const DEFAULT_RULE_ORDER = [
  RULE_TYPE_MAP.CLEAN.value,
  RULE_TYPE_MAP.MODIFY.value,
  RULE_TYPE_MAP.ANNOTATION.value,
];

// 默认运行时间段
export const DEFAULT_EXEC_TIME_START = '00:00';
export const DEFAULT_EXEC_TIME_END = '23:59';

// 运行时间段配置（0-23小时）
export const RUN_TIME_HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  text: `${i.toString().padStart(2, '0')}:00`,
}));

// 根据路由路径获取任务类型
export const getTaskTypeByPath = (pathname: string): number | undefined => {
  const taskType = Object.values(TASK_TYPE_MAP).find((item) => pathname.includes(`/pipeline/${item.pathKey}`));
  return taskType?.value;
};

export const CORPUS_TYPE_MAP = {
  IMAGE: {
    value: 1,
    text: '图片',
    color: 'blue',
  },
  VIDEO: {
    value: 2,
    text: '视频',
    color: 'magenta',
  },
  AUDIO: {
    value: 3,
    text: '音频',
    color: 'lime',
  },
  TEXT: {
    value: 4,
    text: '文本',
    color: 'green',
  },
};

export const CRAWL_TASK_TYPE_MAP = {
  ONE_TIME: {
    value: 1,
    text: '单次执行任务',
    color: 'lime',
  },
  ON_GOING: {
    value: 2,
    text: '持续运行任务',
    color: 'blue',
  },
};

export const CRAWL_TASK_STATUS_MAP = {
  UNKNOWN: {
    value: 0,
    text: '未知',
    color: 'red',
  },
  PENDING: {
    value: 1,
    text: '待运行',
    color: 'gold',
  },
  TRIAL_RUNNING: {
    value: 2,
    text: '试运行中',
    color: 'lime',
  },
  TRIAL_COMPLETED: {
    value: 3,
    text: '试运行完成',
    color: 'success',
  },
  RUNNING: {
    value: 4,
    text: '运行中',
    color: 'lime',
  },
  COMPLETED: {
    value: 5,
    text: '已完成',
    color: 'green',
  },
  OFFLINE: {
    value: 6,
    text: '已下线',
    color: 'red',
  },
};

// 处理链路任务状态（复用现有状态枚举，简化为待执行和进行中）
export const MACHINE_COMMON_STATUS_MAP = {
  PENDING: {
    value: 1,
    text: '待执行',
    color: 'gold',
  },
  RUNNING: {
    value: 4,
    text: '进行中',
    color: 'lime',
  },
};

export const TASK_MODULE_EXEC_MAP = {
  UNKNOWN: {
    value: 0,
    text: '未知',
    color: 'red',
  },
  PENDING: {
    value: 1,
    text: '待运行',
    color: 'gold',
  },
  TRIALRUNNING: {
    value: 2,
    text: '试运行中',
    color: 'lime',
  },
  RIALRUNFINISH: {
    value: 3,
    text: '试运行完成',
    color: 'lime',
  },
  RUNNING: {
    value: 4,
    text: '运行中',
    color: 'green',
  },
  SUCCESS: {
    value: 5,
    text: '成功',
    color: 'green',
  },
  FAILED: {
    value: 6,
    text: '失败',
    color: 'red',
  },
  PARTFAILED: {
    value: 7,
    text: '部分失败',
    color: 'red',
  },
};

export enum TEMPLATE_TYPE_MAP {
  STRING_LIST = 1,
  STRING = 2,
}

export const CORPUS_ITEM_SOURCE_MAP = {
  RED_NOTE: {
    value: 'xhs',
    text: '小红书',
  },
  WEIBO: {
    value: 'wb',
    text: '微博',
  },
  BILIBILI: {
    value: 'bilibili',
    text: 'bilibili',
  },
};

export const OFFLINE_IMPORT_TYPE_MAP = {
  ZIP: {
    value: 1,
    text: 'ZIP',
  },
  EXCEL: {
    value: 2,
    text: 'EXCEL',
  },
  CSV: {
    value: 3,
    text: 'CSV',
  },
};

export enum OfflineImportDataType {
  UNKNOWN = 0,
  ZIP = 1,
  EXCEL = 2,
  CSV = 3,
}

export const TASK_STATUS = {
  PENDING: 0,
  PROCESSING: 1,
  COMPLETED: 2,
  FAILED: 3,
} as const;

// 上传进度阶段
export const UPLOAD_STAGE = {
  VALIDATE: 'validate',
  UPLOAD: 'upload',
  EXTRACT: 'extract',
} as const;

export const SUPPORTED_FILE_TYPES = {
  zip: ['.zip'],
  excel: ['.xlsx', '.xls'],
  csv: ['.csv'],
};

export const offlineImportSheetHeader = [
  ['语料（插入图片或链接）', '作者名', '来源', '语料链接', '语料描述', '自行拓展…'],
];

export const offlineImportCsvSheetHeader = [
  ['语料（插入图片或链接）', '标签（json格式）', '附带内容（json格式）', '语料线索（json格式）'],
];

export const OFFLINE_UPLOAD_GB = 10;

// 任务处理超时时间（2小时，单位：毫秒）- 超过此时间仍未完成则认为任务异常
export const TASK_PROCESSING_TIMEOUT_MS = 2 * 60 * 60 * 1000;

// 任务过期时间（2天，单位：毫秒）- 用于清理历史任务
export const TASK_EXPIRE_MS = 2 * 24 * 60 * 60 * 1000;

// 页面不可见时的轮询间隔（5分钟，单位：毫秒）
export const BACKGROUND_POLL_INTERVAL_MS = 5 * 60 * 1000;

// 模块配置映射（用于 form2CorpusTask 转换）
export const MODULE_CONFIG_MAP: Record<string, IModuleConfig> = {
  clean: {
    type: TASK_TYPE_MAP.CLEAN.value,
    templateField: 'cleanTemplate',
    taskModuleIdField: 'cleanTaskModuleId',
    taskModuleInfoField: 'cleanTaskModuleInfo',
    corpusSetNameField: 'cleanCorpusSetName',
    corpusSetDescField: 'cleanCorpusSetDesc',
    corpusSetIdField: 'cleanCorpusSetId',
    targetCorpusSetModeField: 'cleanTargetCorpusSetMode',
    execTimeListField: 'cleanExecTimeList',
    fieldPrefix: 'clean',
  },
  modify: {
    type: TASK_TYPE_MAP.MACHINE_MODIFY.value,
    templateField: 'machineModifyTemplate',
    taskModuleIdField: 'machineModifyTaskModuleId',
    taskModuleInfoField: 'machineModifyTaskModuleInfo',
    corpusSetNameField: 'machineModifyCorpusSetName',
    corpusSetDescField: 'machineModifyCorpusSetDesc',
    corpusSetIdField: 'machineModifyCorpusSetId',
    targetCorpusSetModeField: 'machineModifyTargetCorpusSetMode',
    execTimeListField: 'machineModifyExecTimeList',
    fieldPrefix: 'modify',
  },
  annotation: {
    type: TASK_TYPE_MAP.MACHINE_LABEL.value,
    templateField: 'annotationTemplateJson',
    taskModuleIdField: 'annotationTaskModuleId',
    taskModuleInfoField: '',
    corpusSetNameField: 'annotationCorpusSetName',
    corpusSetDescField: 'annotationCorpusSetDesc',
    corpusSetIdField: 'annotationCorpusSetId',
    targetCorpusSetModeField: 'annotationTargetCorpusSetMode',
    execTimeListField: 'annotationExecTimeList',
    fieldPrefix: 'annotation',
    useCustomTemplate: true,
    customTemplateField: 'annotationTemplateJson',
  },
};

export const MAX_MODULE_INSTANCES_PER_TYPE = 10;

export const INSTANCE_ID_SEPARATOR = '_';

export const MULTI_UPLOAD_STORAGE_KEY = 'corpus_multi_upload_tasks';
